import { CameraOff, Ticket } from 'lucide-react';

import type { Student } from '~entities/student';

import { StudentNameWithMedicalBadge } from '~shared/components/medical-details';
import { cn } from '~shared/shadcn/lib/utils';
import { Avatar, AvatarFallback, AvatarImage } from '~shared/shadcn/ui/avatar';
import { badgeVariants } from '~shared/shadcn/ui/badge';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '~shared/shadcn/ui/tooltip';

import { getAgeOnDate, getStudentInitials } from '../lib/attendance-student';

interface AttendanceStudentCellProps {
  student: Student;
  attendanceDate: string;
}

export function AttendanceStudentCell({
  student,
  attendanceDate,
}: AttendanceStudentCellProps) {
  const age = getAgeOnDate(student.dob, attendanceDate);
  const recitals = student.recitals ?? [];
  const recitalLabel =
    recitals.length > 1 ? `${recitals.length} recitals` : 'Recital';

  return (
    <div className='flex min-w-0 items-center gap-3'>
      <Avatar className='size-10 shrink-0 border bg-muted'>
        {student.profilePicUrl ? (
          <AvatarImage
            src={student.profilePicUrl}
            alt={student.name}
            className='object-cover'
          />
        ) : null}
        <AvatarFallback className='text-xs font-semibold text-muted-foreground'>
          {getStudentInitials(student.name)}
        </AvatarFallback>
      </Avatar>

      <div className='flex min-w-0 flex-col gap-1'>
        <div className='flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1'>
          <StudentNameWithMedicalBadge
            name={student.name}
            medical={student.medical}
            nameClassName='font-medium'
          />
          {age !== null && (
            <span className='text-xs text-muted-foreground'>Age {age}</span>
          )}
        </div>

        {(recitals.length > 0 || student.photoOptOut) && (
          <TooltipProvider>
            <div className='flex flex-wrap items-center gap-1.5'>
              {recitals.length > 0 && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span
                      tabIndex={0}
                      className={cn(
                        badgeVariants({ variant: 'outline' }),
                        'gap-1 border-violet-200 bg-violet-50 px-1.5 py-0 text-[11px] font-medium text-violet-800 hover:bg-violet-50',
                      )}
                      aria-label={`In ${recitalLabel.toLowerCase()}: ${recitals
                        .map((recital) => recital.name)
                        .join(', ')}`}
                    >
                      <Ticket className='size-3' aria-hidden='true' />
                      {recitalLabel}
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>
                    <ul className='list-none p-0'>
                      {recitals.map((recital) => (
                        <li key={recital.recitalId}>{recital.name}</li>
                      ))}
                    </ul>
                  </TooltipContent>
                </Tooltip>
              )}

              {student.photoOptOut && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span
                      tabIndex={0}
                      className={cn(
                        badgeVariants({ variant: 'outline' }),
                        'gap-1 border-amber-200 bg-amber-50 px-1.5 py-0 text-[11px] font-medium text-amber-900 hover:bg-amber-50',
                      )}
                      aria-label='No photos or video allowed'
                    >
                      <CameraOff className='size-3' aria-hidden='true' />
                      No photos
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>No photos/video allowed</TooltipContent>
                </Tooltip>
              )}
            </div>
          </TooltipProvider>
        )}
      </div>
    </div>
  );
}
